import * as React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { interpolate } from '../../utils/string';
import ProjectDropdown from './ProjectDropdown';
import { PanelType, Project, PROJECT_STATUS } from '@nexxt/common/types';
import { STATIC_STORAGE_URL } from '@nexxt/common/constants';
import { uppercaseFirstLetter } from '@nexxt/common/services/TextService';
import { EDIT_NEW_PROJECT } from '../../constants/routes';
import { ILocation } from '../../interface/common.interface';
import config from '../../config';
import { TENANT_LEVEL } from '@nexxt/common/types';
import {
    getAccessDevMode,
    getTenantLevel
} from '@nexxt/common/firebase/auth/userDetails';
import DirectoryCard from './DirectoryCard';

interface ICardProps {
    key: number;
    allProjects: any;
    tenantId: number;
    location: ILocation;
    getProjects: (refresh?: boolean) => void;
    toggleShareLinkModal: (project: Project) => void;
    isShareLinksModalOpen: boolean;
    project: Project;
    changePath: (path: string) => void;
    clearState: () => void;
    duplicateProjectToggle: () => void;
    setDuplicateProject: (project) => void;
    hideDate?: boolean;
    cardType?: 'full' | 'simple';
    clientTerm?: string;
    showWatermark?: boolean;
}

class ProjectCard extends React.Component<ICardProps, null> {
    redirectToChat = (project: Project) => {
        const isWizard = getTenantLevel() === TENANT_LEVEL.INCA_WIZARD;
        window.location.assign(
            `${config.host.bot}/p/${project.codeName}${
                isWizard ? '?TERMINATE=cta&testMode=true' : ''
            }`
        );
    };

    redirectToEditPage = (project: Project) => {
        const isWizard = getTenantLevel() === TENANT_LEVEL.INCA_WIZARD;

        if (project.tenantID !== -1 && isWizard) {
            this.redirectToChat(project);
        } else if (
            [
                PROJECT_STATUS.LIVE,
                PROJECT_STATUS.PAUSED,
                PROJECT_STATUS.COMPLETE
            ].includes(project.status)
        ) {
            window.location.assign(
                `${config.host.dashboard}/p/${project.codeName}${
                    isWizard ? '/reports' : ''
                }`
            );
        } else {
            this.props.changePath(
                interpolate(EDIT_NEW_PROJECT, {
                    projectId: project.id
                })
            );
        }
    };
    render() {
        const {
            allProjects,
            project,
            tenantId,
            cardType,
            getProjects,
            clearState,
            duplicateProjectToggle,
            setDuplicateProject,
            toggleShareLinkModal,
            clientTerm,
            showWatermark
        } = this.props;
        const isDevAllowedAccount = getAccessDevMode();
        const withIncaPanel =
            project.panelSettings?.panelType === PanelType.INCA;
        const tenantLevel = getTenantLevel();
        const isGlobal = project.tenantID === -1;
        const fullCard = cardType !== 'simple';

        const isSandboxTenant = tenantLevel === TENANT_LEVEL.SANDBOX;
        const isWizard = tenantLevel === TENANT_LEVEL.INCA_WIZARD;
        const hasLaunched = [
            PROJECT_STATUS.LIVE,
            PROJECT_STATUS.PAUSED,
            PROJECT_STATUS.COMPLETE
        ].includes(project.status);

        return (
            <DirectoryCard
                cardType={cardType}
                cardLink={() => this.redirectToEditPage(project)}
                status={project.status}
                name={project.name}
                clientName={project.client}
                clientImage={
                    project.clientLogo ||
                    `${STATIC_STORAGE_URL}/img/inca_black.png`
                }
                lastUpdated={project.lastModifiedOn?.substring(0, 10)}
                lastUpdatedBy={project.lastModifiedBy.split('null')[0]}
                clientTerm={clientTerm}
                showWatermark={showWatermark}
            >
                <React.Fragment>
                    <div
                        className="cursor-pointer uppercase font-bold text-sm text-blue hover:text-blue"
                        onClick={(e) => {
                            e.preventDefault();
                            this.redirectToEditPage(project);
                        }}
                    >
                        {hasLaunched || isGlobal ? 'Dashboard' : 'Edit'}
                    </div>
                    {isGlobal && (
                        <div
                            className="text-blue inca-card-button"
                            onClick={(e) => {
                                e.preventDefault();
                                this.redirectToChat(project);
                            }}
                        >
                            Chat
                        </div>
                    )}
                    {hasLaunched && !withIncaPanel ? (
                        <div
                            className="inca-card-button"
                            onClick={() =>
                                this.props.changePath(
                                    `${interpolate(EDIT_NEW_PROJECT, {
                                        projectId: project.id
                                    })}/audience`
                                )
                            }
                        >
                            Audience
                        </div>
                    ) : null}
                    {hasLaunched && !isGlobal && (
                        <div
                            className="inca-card-button"
                            onClick={() => toggleShareLinkModal(project)}
                        >
                            Links
                        </div>
                    )}

                    <div className="ml-auto">
                        {fullCard && (
                            <ProjectDropdown
                                project={project}
                                allProjects={allProjects}
                                getProjects={getProjects}
                                tenantId={tenantId}
                                isGlobal={isGlobal}
                                clearState={clearState}
                                duplicateProjectToggle={duplicateProjectToggle}
                                setDuplicateProject={setDuplicateProject}
                                disabled={hasLaunched && isSandboxTenant}
                            />
                        )}
                    </div>
                </React.Fragment>
            </DirectoryCard>
        );
    }
}

const mapStateToProps = (store) => {
    const {
        router: { location }
    } = store;
    return {
        location
    };
};

const mapDispatchToProps = (dispatch) => ({
    changePath: (path) => {
        dispatch(push(path));
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectCard);
